import { useState, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import FixtureRow from './FixtureRow';
import FixtureBooking from './FixtureBooking';
import SortFixtures from '../utils/SortFixtures';

const FixtureList = ({ fixtures, labels, siteSettings, viewSettings }) => {
  const { tableLayout } = labels;

  function allreadyPlayed(matchDate) {
    const today = new Date();
    const matchDateAsDate = new Date(matchDate);
    return today.getTime() > matchDateAsDate.getTime();
  }

  function getDefaultSelection() {
    let defaultSelection = '';
    fixtures.find(({ base, full, opponentCode, matchDate }) => {
      const notPlayed = !allreadyPlayed(matchDate);
      if (base.status === 'in sale' && notPlayed) {
        defaultSelection = `${opponentCode}_base`;
        return true;
      }
      if (full.status === 'in sale' && notPlayed) {
        defaultSelection = `${opponentCode}_full`;
        return true;
      }
      return false;
    });
    return defaultSelection;
  }

  const [selected, setSelected] = useState(getDefaultSelection());
  const [travelAges, setTravelAges] = useState(['42', '42']);
  const [isMember, setIsMember] = useState(false);

  const handleSelected = (fixture, selectedPackage) => {
    setSelected((prevState) => {
      const { base, full, opponentCode: row } = fixture;
      let packageType;
      if (selectedPackage && selectedPackage !== '') {
        // Clicked on radiobutton
        packageType = selectedPackage.slice(5);
      } else {
        // Clicked on row
        packageType = prevState.length ? prevState.slice(5) : 'base';
      }
      if (
        fixture[packageType].status === 'in sale' ||
        fixture[packageType].status === 'ineligible'
      ) {
        return `${row}_${packageType}`;
      }
      if (base.status === 'in sale' || base.status === 'ineligible') {
        return `${row}_base`;
      }
      if (full.status === 'in sale' || full.status === 'ineligible') {
        return `${row}_full`;
      }
      return '';
    });
  };

  const handleTravelAges = useCallback(
    (value) => {
      setTravelAges(value);
    },
    [setTravelAges],
  );

  const handleMembership = (value) => {
    setIsMember(value);
  };

  const sortedFixtures = SortFixtures(fixtures, viewSettings);

  const filteredFixtures = sortedFixtures
    .map((item) => {
      const played = allreadyPlayed(item.matchDate);
      return {
        ...item,
        played: played,
        unavailable: played || item.completelySoldOut,
      };
    })
    .filter((fixture) => {
      if (
        (viewSettings.hideSoldout && fixture.completelySoldOut) ||
        (viewSettings.hidePast && fixture.played)
      ) {
        return false;
      }
      return true;
    })
    .map((fixture) => (
      <Fragment key={fixture.opponentCode}>
        <FixtureRow
          fixture={fixture}
          labels={labels}
          selected={selected}
          handleSelected={handleSelected}
          siteSettings={siteSettings}
        />
        {selected.includes(fixture.opponentCode) && (
          <FixtureBooking
            fixture={fixture}
            labels={labels}
            selectedFixture={selected}
            siteSettings={siteSettings}
            travelAges={travelAges}
            isMember={isMember}
            handleTravelAges={handleTravelAges}
            handleMembership={handleMembership}
          />
        )}
      </Fragment>
    ));

  if (filteredFixtures.length) {
    return filteredFixtures;
  }
  // No fixture results
  return (
    <tr className="no-fixtures">
      <td colSpan="4">
        <div>
          {tableLayout.noSearchResults || 'tableLayout.noSearchResults'}
        </div>
      </td>
    </tr>
  );
};

FixtureList.propTypes = {
  fixtures: PropTypes.arrayOf(PropTypes.shape()),
  labels: PropTypes.shape({
    tableLayout: PropTypes.shape({
      noSearchResults: PropTypes.string,
    }),
  }),
  siteSettings: PropTypes.shape(),
  viewSettings: PropTypes.shape({
    hidePast: PropTypes.bool,
    hideSoldout: PropTypes.bool,
  }),
};

FixtureList.defaultProps = {
  fixtures: [],
  labels: null,
  siteSettings: null,
  viewSettings: null,
};

export default FixtureList;
